import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    isWishListLineDeactivated,
    isWishListLineDiscontinued,
    isWishListLineRestricted,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import setWishListLineIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetWishListLineIsSelected";
import updateQuantity from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateQuantity";
import updateUnitOfMeasure from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateUnitOfMeasure";
import updateWishListLineQuantities from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLineQuantities";
import translate from "@insite/client-framework/Translate";
import { ProductModel, WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import MyListDetailsDiscontinuedProductListLine from "@insite/content-library/Widgets/MyListsDetails/MyListDetailsDiscontinuedProductListLine";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import Edit from "@insite/mobius/Icons/Edit";
import RefreshCw from "@insite/mobius/Icons/RefreshCw";
import Trash2 from "@insite/mobius/Icons/Trash2";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu from "@insite/mobius/OverflowMenu";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomContactUsForm from "@tCooneyBrothers/Components/CustomContactUsForm";
import LazyImage from "@tCooneyBrothers/MobiusOverrides/LazyImage";
import ProductAddToCartButton from "@tCooneyBrothers/Overrides/Components/ProductAddToCartButton";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    wishList: WishListModel;
    wishListLine: WishListLineModel;
    productInfo: ProductInfo;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
    onEditNotesClick: (wishListLine: WishListLineModel) => void;
    showImageBadges: boolean;
    maximumNumberOfImageBadges: number;
    showTextBadges: boolean;
    maximumNumberOfTextBadges: number;
    onViewReplacementProduct?: (wishListLine: WishListLineModel) => void;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSelected: !!state.pages.myListDetails.selectedWishListLineIds.find(o => o === ownProps.wishListLine.id),
    settingsCollection: getSettingsCollection(state),
    isQuantityUpdated: state.pages.myListDetails.wishListLinesWithUpdatedQuantity[ownProps.wishListLine.id],
});

const mapDispatchToProps = {
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    updateWishListLineQuantities,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsProductListLineStyles {
    editNotesLink?: LinkPresentationProps;
    lineInnerContainer?: GridContainerProps;
    partnumbersGridContainer?: GridContainerProps;
    selectedCheckboxHidden?: HiddenProps;
    productContex?: InjectableCss;
    imageGridItem?: GridItemProps;
    productGridItem?: GridItemProps;
    selectedCheckbox?: CheckboxPresentationProps;
    imageWrapper?: InjectableCss;
    editNotesHidden?: HiddenProps;
    productImageStyles?: ProductImageStyles;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    notesStyles?: SmallHeadingAndTextStyles;
    overflowMenuGridItem?: GridItemProps;
    partNumbersHidden?: HiddenProps;
    partNumbersHiddenGridItem?: HiddenProps;
    partNumbersGridItem?: GridItemProps;
    addEditNotesGridItem?: GridItemProps;
    priceAndAvailabilityGridItem?: GridItemProps;
    hideenPriceAndAvailabilityGridItem?: GridItemProps;
    selectGridContainer?: GridContainerProps;
    selectGridItem?: GridItemProps;
    purchasedDateGridItem?: GridItemProps;
    purchasedDateLabelText?: TypographyPresentationProps;
    purchasedDateText?: TypographyPresentationProps;
    deleteUpdateGridItem?: GridItemProps;
    partNumberNotesGridItem?: GridItemProps;
    addNotesGridItem?: GridItemProps;
    priceAndAvailabilityInnerContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    uomAndQuantityGridItem?: GridItemProps;
    uomAndQuantityInnerContainer?: GridContainerProps;
    uomGridItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityGridItem?: GridItemProps;
    extPriceGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    availabilityGridItem?: GridItemProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    notesHidden?: HiddenProps;
    notesGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    removeRestrictedButton?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    deleteLinkHidden?: HiddenProps;
    deleteLink?: LinkPresentationProps;
    updateQuantityWrapper?: InjectableCss;
    updateQuantityLink?: LinkPresentationProps;
    quantityUpdatedIcon?: IconPresentationProps;
    quantityUpdatedText?: TypographyPresentationProps;
    restrictedDescriptionGridItem?: GridItemProps;
    restrictedDescriptionText?: TypographyPresentationProps;
    restrictedMessageText?: TypographyPresentationProps;
    restrictedRemoveItemLink?: LinkPresentationProps;
    removeCartLineIcon?: IconPresentationProps;
    textBadgeStyles?: ProductTextBadgesStyles;
    imageBadgeStyles?: ProductImageBadgesStyles;
    rfqTextItem?: GridItemProps;
    rfqText?: TypographyPresentationProps;
    rfqButton?: ButtonPresentationProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
    rfqButtonsGridItem?: GridItemProps;
}

export const productListLineStyles: MyListsDetailsProductListLineStyles = {
    editNotesLink: {
        typographyProps: { size: 12 },
        css: css`
            // margin-bottom: 5px;
            display: flex;
            align-items: center;
            span {
                display: flex;
                color: #2450a8 !important;
            }
            [class^="IconWrapper"] {
                margin-right: 4px;
            }
        `,
    },
    lineInnerContainer: {
        gap: 10,
        css: css`
            position: relative;
        `,
    },
    partnumbersGridContainer: {
        css: css`
            > [class^="GridOffset"] {
                align-items: center;
            }
        `,
    },
    selectedCheckboxHidden: { below: "md" },
    imageGridItem: {
        width: [12, 12, 2, 2, 1],
        printWidth: 1,
        css: css`
            display: block;
        `,
    },
    selectedCheckbox: {
        sizeVariant: "small",
        css: css`
            @media print {
                display: none;
            }
            margin-right: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
            }
            width: 100%;
            min-width: 0;
            flex-direction: column;
        `,
    },
    editNotesHidden: { below: "md" },
    productGridItem: {
        width: [12, 12, 10, 10, 11],
        css: css`
            flex-direction: column;
        `,
    },
    descriptionGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 5,
        css: css`
            padding-left: 0;
            padding-right: 0;
            flex-direction: column;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-left: 15px;
                            padding-right: 15px;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    productPartNumbersStyles: {
        erpNumberGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        erpNumberLabelText: { variant: "legend" },
        customerNameGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        customerNameLabelText: { variant: "legend" },
        manufacturerItemGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        manufacturerItemLabelText: { variant: "legend" },
    },
    overflowMenuGridItem: {
        width: [1, 1, 0, 0, 0],
        printWidth: 0,
    },
    partNumbersHidden: {
        below: "sm",
        css: css`
            @media print {
                display: block;
            }
            width: 100%;
        `,
    },
    partNumbersHiddenGridItem: {
        css: css`
            width: 100%;
        `,
    },

    partNumbersGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 5,
        css: css`
            padding-left: 0;
        `,
    },
    addEditNotesGridItem: {
        width: 12,
        css: css`
            @media print {
                display: none;
            }
            padding: 10px 0;
        `,
    },
    priceAndAvailabilityGridItem: {
        width: [12, 12, 7, 6, 5],
        printWidth: 7,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-top: 0;
                            padding-bottom: 0;
                        `,
                        css`
                            padding-top: 0;
                            padding-bottom: 0;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    hideenPriceAndAvailabilityGridItem: {
        width: [12, 12, 7, 5, 5],
        printWidth: 7,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-top: 0;
                            padding-bottom: 0;
                            max-width: 100% !important;
                        `,
                        css`
                            padding-top: 0;
                            padding-bottom: 0;
                            max-width: 100% !important;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    selectGridContainer: {
        css: css`
            margin-bottom: 5px;
        `,
    },
    selectGridItem: {
        width: [12, 12, 2, 2, 1],
        css: css`
            display: flex;
            align-items: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-bottom: 30px;
                        `,
                        css`
                            padding-bottom: 30px;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    deleteUpdateGridItem: {
        width: [12, 12, 10, 10, 11],
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    addNotesGridItem: {
        width: [12, 12, 5, 5, 5],
        css: css`
            padding-left: 0;
            padding-right: 0;
        `,
    },
    priceAndAvailabilityInnerContainer: { gap: 10 },
    purchasedDateGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    purchasedDateLabelText: {
        variant: "p",
        transform: "uppercase",
        weight: 600,
        css: css`
            @media print {
                font-size: 12px;
            }
        `,
    },
    priceGridItem: {
        width: [12, 12, 4, 3, 5],
        printWidth: 3,
        css: css`
            flex-direction: column;
            section > div {
                flex-direction: column;
                [data-test-selector="productPrice_label"],
                [data-test-selector="productPrice_unitNetPrice"] {
                    margin: 0;
                }

                [data-test-selector="productPrice_label"] {
                    font-size: 12px;
                }
            }
        `,
    },
    productPriceStyles: {
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            weight: 600,
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: { typographyProps: { size: 12 } },
    },
    uomAndQuantityGridItem: {
        width: [12, 12, 8, 7, 7],
        printWidth: 9,
        css: css`
            display: flex;
            align-items: center;
            margin: 0;
        `,
    },
    uomAndQuantityInnerContainer: {
        gap: 10,
        css: css`
            @media print {
                input,
                select {
                    height: 18px !important;
                    font-size: 11px !important;
                }
            }
        `,
    },
    uomGridItem: { width: 9, printWidth: 6 },
    quantityGridItem: {
        width: [4, 4, 6, 6, 6],
        printWidth: 6,
        css: css`
            flex-direction: column;
            align-items: center;
            position: relative;
            button {
                position: absolute;
                left: 50%;
                transform: translateX(-50%);
                bottom: 2px;
                width: 100px;
                display: flex;
                justify-content: center;
            }

            [class^="FormFieldStyle"] {
                background-color: #e3e3e3;
                flex-direction: row;
                -webkit-box-align: center;
                align-items: center;
                padding: 5px;
                margin-bottom: 10px;
                label {
                    position: static !important;
                    background: transparent !important;
                    font-weight: normal;
                    font-size: 12px;
                    padding: 6px;
                    width: auto !important;
                    white-space: nowrap;
                }
                [class^="FormInputWrapper"] {
                    margin: 0;
                    input {
                        height: 30px;
                        border-radius: 4px;
                        text-align: center;
                        @media print {
                            width: 65px;
                        }
                    }
                }
            }
        `,
    },
    extPriceGridItem: {
        width: 6,
        printWidth: 6,
        css: css`
            flex-direction: column;
            padding-left: 30px;
            // align-items: flex-end;
            margin-left: auto !important;
            font-size: 12px;
            span {
                &:last-child {
                    color: #2450a8;
                    font-weight: bold;
                }
            }
        `,
    },
    productQuantityOrderedStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        cssOverrides: {
            inputSelect: css`
                @media print {
                    width: 40px;
                }
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        iconProps: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    availabilityGridItem: {
        width: [12, 12, 4, 4, 5],
        printWidth: 4,
        css: css`
            [data-test-selector="availability_wrapper"] {
                padding: 15px 0;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-top: 0;
                            padding-bottom: 0;
                        `,
                        css`
                            padding-top: 0;
                            padding-bottom: 0;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    removeRestrictedButton: {
        width: [12, 12, 3, 3, 2],
        printWidth: 0,
        css: css`
            flex-direction: column;
            align-items: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            position: absolute;
                            right: 5%;
                        `,
                        css`
                            max-width: fit-content;
                            padding-left: 35px;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    buttonsGridItem: {
        width: [12, 12, 3, 3, 2],
        printWidth: 0,
        css: css`
            flex-direction: column;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-left: 15px !important;
                        `,
                        css`
                            padding-left: 0;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    notesHidden: {
        above: "sm",
        css: css`
            @media print {
                display: none;
            }
            width: 100%;
        `,
    },
    notesGridItem: {
        width: 12,
        css: css`
            padding: 0;
            [data-test-selector="heading"] {
                display: none;
            }
        `,
    },
    addToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    deleteLinkHidden: {
        below: "sm",
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            align-items: flex-end;
        `,
    },
    deleteLink: {
        // typographyProps: { size: 12 },
        css: css`
            margin-top: 10px;
            span {
                display: flex;
                align-items: flex-end;
            }
        `,
    },
    updateQuantityWrapper: {
        css: css`
            display: flex;
            margin-top: 10px;
        `,
    },
    updateQuantityLink: {
        typographyProps: { size: 12 },
        icon: {
            iconProps: {
                src: RefreshCw,
                size: 12,
            },
        },
    },
    quantityUpdatedIcon: {
        src: Check,
        size: 16,
        css: css`
            margin-right: 5px;
        `,
    },
    quantityUpdatedText: {
        size: 12,
    },
    restrictedDescriptionText: {
        css: css`
            padding-right: 5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-top: 20px;
                        `,
                        css`
                            padding-top: 20px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },

    textBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
    },
    imageBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
        imageItem: {
            css: css`
                margin: 5px 0;
            `,
        },
    },

    removeCartLineIcon: {
        src: Trash2,
        color: "#ef3742",
        css: css`
            width: 20px;
            height: 20px;
            &:hover {
                opacity: 0.7;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            position: absolute;
                            right: -15px;
                            top: 10px;
                        `,
                        css`
                            position: absolute;
                            right: -15px;
                            top: 10px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    restrictedDescriptionGridItem: {
        width: [12, 12, 6, 6, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    restrictedMessageText: { color: "warning" },
    rfqTextItem: {
        width: 12,
        printWidth: 12,
        css: css``,
    },
    rfqText: {
        css: css`
            font-size: 14px;
            font-weight: bold;
            color: #2450a8;
        `,
    },
    rfqButton: {
        css: css`
            cursor: pointer;
            background: rgb(88, 180, 108);
            border: 2px solid rgb(88, 180, 108);
            color: #141414;
            padding: 0 25px;
            min-width: 80%;
            &:hover {
                background: rgb(88, 180, 108);
                border: 2px solid rgb(88, 180, 108);
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            min-width: 85%;
                            margin-top: 2px;
                        `,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    successModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
                border-bottom: 3px solid #ef3742;
                background: #fafafa;
            `,
            modalContent: css`
                padding: 30px;
                padding-top: 0;
            `,
            modalBody: css`
                max-height: calc(100% - 40px) !important;
            `,
        },
    },
    rfqButtonsGridItem: {
        width: [12, 12, 3, 3, 2],
        printWidth: 0,
        css: css`
            flex-direction: column;
            align-items: center;
            margin-left: auto !important;
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, css``, css``, null], "max")}
        `,
    },
    messageTextGridItem: {
        width: 12,
    },
};

const styles = productListLineStyles;

const MyListsDetailsProductListLine: React.FC<Props> = ({
    wishList,
    wishListLine,
    productInfo,
    isSelected,
    settingsCollection,
    isQuantityUpdated,
    onDeleteClick,
    onEditNotesClick,
    updateWishListLineQuantities,
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    showImageBadges,
    showTextBadges,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
    onViewReplacementProduct,
}) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const product = mapWishListLineToProduct(wishListLine);

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setWishListLineIsSelected({ wishListLineId: wishListLine.id, isSelected: value });
    };

    const onUpdateQuantityClick = () => {
        updateWishListLineQuantities({ reloadWishListLines: true });
    };

    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    const { productSettings } = settingsCollection;
    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: qtyOrdered => {
            updateQuantity({ wishListLineId: wishListLine.id, qtyOrdered });
        },
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateUnitOfMeasure({ wishListLineId: wishListLine.id, unitOfMeasure });
        },
    };

    const availability = productInfo?.inventory?.inventoryAvailabilityDtos?.[0].availability;
    const quantity = productInfo?.qtyOrdered;
    const productTitle = product.productTitle;
    const productNumber = product.productNumber;
    const brandName = product.brand?.name;

    const donotDisplayPrice = wishListLine?.properties?.donotDisplayPrice?.toLowerCase() === "true" || false;
    const discontinuedProduct = wishListLine?.properties?.discontinuedProduct?.toLowerCase() === "true" || false;

    const onButtonClickHandler = () => {
        setIsModalOpen(true);
    };
    const continueButtonClickHandlerToParent = (modalState: boolean) => {
        modalState = false;
        setIsModalOpen(modalState);
        clearForm();
    };
    const contactUsModalCloseHandler = () => setIsModalOpen(false);

    if (isWishListLineDiscontinued(wishListLine, productInfo) && wishListLine.isActive) {
        return (
            <MyListDetailsDiscontinuedProductListLine
                wishList={wishList}
                wishListLine={wishListLine}
                productContext={productContext}
                isSelected={isSelected}
                selectChangeHandler={selectChangeHandler}
                onDeleteClick={onDeleteClick}
                onViewReplacementProduct={onViewReplacementProduct}
            />
        );
    }

    if (isWishListLineRestricted(wishListLine, productInfo) || isWishListLineDeactivated(wishListLine)) {
        return (
            <GridContainer
                {...styles.lineInnerContainer}
                className="wishlist-restricted-line-grid-container"
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <GridItem
                    {...styles.restrictedDescriptionGridItem}
                    className="wishlist-restricted-line-description-grid-item"
                >
                    <Typography
                        {...styles.restrictedDescriptionText}
                        className="wishlist-restricted-line-description-text"
                    >
                        {wishListLine.shortDescription}
                    </Typography>
                </GridItem>
                <Hidden {...styles.partNumbersHiddenGridItem} above="sm">
                    <GridItem
                        {...styles.partNumbersGridItem}
                        className="wishlist-restricted-line-part-number-grid-item"
                    >
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                    </GridItem>
                </Hidden>
                <GridItem {...styles.hideenPriceAndAvailabilityGridItem}>
                    <Typography
                        {...styles.restrictedMessageText}
                        className="wishlist-restricted-line-message-text"
                        data-test-selector="restrictedMessage"
                    >
                        {isWishListLineRestricted(wishListLine, productInfo) &&
                            !isWishListLineDeactivated(wishListLine) && (
                                <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}</>
                            )}
                        {isWishListLineDeactivated(wishListLine) && (
                            <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Deactivation")}</>
                        )}
                    </Typography>
                </GridItem>
                <GridItem {...styles.removeRestrictedButton}>
                    {canEditWishList && (
                        <Link
                            className="wishlist-restricted-line-remove-link"
                            {...styles.restrictedRemoveItemLink}
                            onClick={() => onDeleteClick(wishListLine)}
                            data-test-selector="removeRestricted"
                        >
                            <IconMemo {...styles.removeCartLineIcon} />
                        </Link>
                    )}
                </GridItem>
            </GridContainer>
        );
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <ProductContext.Provider value={productContext}>
            <GridContainer {...styles.selectGridContainer}>
                <GridItem {...styles.selectGridItem}>
                    <Hidden {...styles.selectedCheckboxHidden}>
                        <Checkbox
                            {...styles.selectedCheckbox}
                            checked={isSelected}
                            onChange={selectChangeHandler}
                            hiddenIconText={translate("Select")}
                            data-test-selector="myListDetails_selectRowCheckbox"
                        >
                            Select
                        </Checkbox>
                    </Hidden>
                </GridItem>

                <GridItem {...styles.deleteUpdateGridItem}>
                    {canEditWishList && (
                        <>
                            <Hidden {...styles.deleteLinkHidden}>
                                <Link
                                    {...styles.deleteLink}
                                    className="wishlist-line-delete-link"
                                    onClick={() => onDeleteClick(wishListLine)}
                                    data-test-selector="deleteListItem"
                                >
                                    <IconMemo {...styles.removeCartLineIcon} />
                                    {/* {translate("Delete")} */}
                                </Link>
                            </Hidden>
                        </>
                    )}
                </GridItem>
            </GridContainer>
            <GridContainer
                {...styles.lineInnerContainer}
                className="wishlist-line-grid-container"
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <GridItem {...styles.imageGridItem}>
                    <StyledWrapper {...styles.imageWrapper} className="wishlist-line-product-image-wrapper">
                        <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.productGridItem}>
                    <GridContainer>
                        <GridItem {...styles.descriptionGridItem} className="wishlist-line-description-grid-item">
                            <ProductDescription
                                product={productContext}
                                extendedStyles={styles.productDescriptionStyles}
                            />
                            <Hidden {...styles.notesHidden}>
                                <GridItem
                                    {...styles.partNumbersGridItem}
                                    className="wishlist-line-product-part-number-grid-item"
                                >
                                    <ProductPartNumbers
                                        productNumber={product.productNumber}
                                        customerProductNumber={product.customerProductNumber}
                                        manufacturerItem={product.manufacturerItem}
                                        extendedStyles={styles.productPartNumbersStyles}
                                    />
                                </GridItem>
                            </Hidden>

                            {wishListLine.notes && (
                                <Hidden {...styles.notesHidden}>
                                    <GridItem {...styles.notesGridItem} className="wishlist-line-notes-grid-item">
                                        <SmallHeadingAndText
                                            heading={translate("Notes")}
                                            text={wishListLine.notes}
                                            extendedStyles={styles.notesStyles}
                                        />
                                    </GridItem>
                                </Hidden>
                            )}
                        </GridItem>
                        <GridItem {...styles.priceAndAvailabilityGridItem}>
                            <GridContainer
                                {...styles.priceAndAvailabilityInnerContainer}
                                className="wishlist-line-price-availability-grid-container"
                            >
                                {wishList.isAutogenerated && (
                                    <GridItem
                                        {...styles.purchasedDateGridItem}
                                        className="wishlist-line-purchased-date-grid-container"
                                    >
                                        <Typography
                                            {...styles.purchasedDateLabelText}
                                            className="wishlist-line-purchased-date-label-text"
                                        >
                                            {translate("Purchased Date")}
                                        </Typography>
                                        <Typography
                                            {...styles.purchasedDateText}
                                            className="wishlist-line-purchased-date-text"
                                            data-test-selector="purchasedDate"
                                        >
                                            <LocalizedDateTime dateTime={wishListLine.createdOn} />
                                        </Typography>
                                    </GridItem>
                                )}
                                {donotDisplayPrice && (
                                    <GridItem {...styles.rfqTextItem}>
                                        <Typography {...styles.rfqText}>
                                            {translate("Pricing Available Upon Request")}
                                        </Typography>
                                    </GridItem>
                                )}
                                {!discontinuedProduct && !donotDisplayPrice && (
                                    <GridItem {...styles.priceGridItem} className="wishlist-line-price-grid-container">
                                        <ProductPrice
                                            product={productContext}
                                            showSavings={true}
                                            showSavingsAmount={productSettings.showSavingsAmount}
                                            showSavingsPercent={productSettings.showSavingsPercent}
                                            extendedStyles={styles.productPriceStyles}
                                        />
                                        <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                                    </GridItem>
                                )}
                                {!donotDisplayPrice && (
                                    <GridItem {...styles.uomAndQuantityGridItem}>
                                        <GridContainer
                                            {...styles.uomAndQuantityInnerContainer}
                                            className="wishlist-line-uom-quantity-grid-container"
                                        >
                                            {product.unitOfMeasures && product.unitOfMeasures.length > 1 && (
                                                <GridItem {...styles.uomGridItem}>
                                                    <ProductUnitOfMeasureSelect
                                                        disabled={!canEditWishList}
                                                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                                    />
                                                </GridItem>
                                            )}
                                            <GridItem
                                                {...styles.quantityGridItem}
                                                className="wishlist-line-quantity-grid-item"
                                            >
                                                <ProductQuantityOrdered
                                                    extendedStyles={styles.productQuantityOrderedStyles}
                                                />
                                                <StyledWrapper
                                                    {...styles.updateQuantityWrapper}
                                                    className="wishlist-line-update-quantity-wrapper"
                                                >
                                                    {productInfo.qtyOrdered > 0 &&
                                                        wishListLine.qtyOrdered !== productInfo.qtyOrdered && (
                                                            <Link
                                                                {...styles.updateQuantityLink}
                                                                className="wishlist-line-update-quantity-link"
                                                                onClick={() => onUpdateQuantityClick()}
                                                            >
                                                                {translate("Update QTY")}
                                                            </Link>
                                                        )}
                                                    {isQuantityUpdated &&
                                                        wishListLine.qtyOrdered === productInfo.qtyOrdered && (
                                                            <>
                                                                <Icon
                                                                    {...styles.quantityUpdatedIcon}
                                                                    className="wishlist-line-quantity-updated-icon"
                                                                />
                                                                <Typography
                                                                    {...styles.quantityUpdatedText}
                                                                    className="wishlist-line-quantity-updated-text"
                                                                >
                                                                    {translate("QTY updated")}
                                                                </Typography>
                                                            </>
                                                        )}
                                                </StyledWrapper>
                                            </GridItem>
                                            {!discontinuedProduct && (
                                                <GridItem {...styles.extPriceGridItem}>
                                                    <Typography as="strong">{translate("EXT PRICE: ")}</Typography>
                                                    <Typography>
                                                        {productInfo.pricing!.extendedUnitNetPriceDisplay}
                                                    </Typography>
                                                </GridItem>
                                            )}
                                        </GridContainer>
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.partnumbersGridContainer}>
                        <GridItem {...styles.partNumbersGridItem}>
                            <Hidden {...styles.partNumbersHidden}>
                                <ProductPartNumbers
                                    productNumber={product.productNumber}
                                    customerProductNumber={product.customerProductNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    extendedStyles={styles.productPartNumbersStyles}
                                />
                            </Hidden>
                        </GridItem>
                        {!discontinuedProduct && !donotDisplayPrice && (
                            <GridItem {...styles.availabilityGridItem} className="wishlist-line-availability-grid-item">
                                <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                            </GridItem>
                        )}
                        <Hidden above="sm">
                            {discontinuedProduct && (
                                <GridItem width={12}>
                                    <LazyImage src="/UserFiles/Images/discontinued-sm.png" />
                                </GridItem>
                            )}
                        </Hidden>
                        {donotDisplayPrice && (
                            <GridItem {...styles.rfqButtonsGridItem} className="wishlist-line-rfq-button-grid-item">
                                <Button {...styles.rfqButton} onClick={onButtonClickHandler}>
                                    {translate("Request Quote")}
                                </Button>
                                <Modal
                                    headline={translate("Request Quote")}
                                    contentLabel="Request Quote"
                                    {...styles.successModal}
                                    isOpen={isModalOpen}
                                    handleClose={contactUsModalCloseHandler}
                                >
                                    <GridContainer {...styles.modalContainer}>
                                        <GridItem {...styles.messageTextGridItem}>
                                            <CustomContactUsForm
                                                availability={availability}
                                                parentModalState={continueButtonClickHandlerToParent}
                                                productQty={quantity}
                                                productTitle={productTitle}
                                                productNumber={productNumber}
                                                brandName={brandName}
                                                isRfq={true}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </Modal>
                            </GridItem>
                        )}
                        {!donotDisplayPrice && (
                            <GridItem {...styles.buttonsGridItem} className="wishlist-line-buttons-grid-item">
                                <ProductAddToCartButton
                                    notes={wishListLine.notes}
                                    extendedStyles={styles.addToCartButton}
                                    data-test-selector="addToCart"
                                />
                            </GridItem>
                        )}
                        <Hidden below="md">
                            {discontinuedProduct && (
                                <GridItem width={12}>
                                    <LazyImage src="/UserFiles/Images/discontinued-sm.png" />
                                </GridItem>
                            )}
                        </Hidden>
                    </GridContainer>
                    <GridContainer>
                        <GridItem {...styles.addNotesGridItem}>
                            <Hidden {...styles.partNumbersHidden}>
                                {canEditWishList && (
                                    <GridItem {...styles.addEditNotesGridItem}>
                                        <Hidden {...styles.editNotesHidden}>
                                            <Link
                                                {...styles.editNotesLink}
                                                className="wishlist-line-edit-notes-link"
                                                onClick={() => onEditNotesClick(wishListLine)}
                                                data-test-selector="myListDetails_addOrEditNote"
                                            >
                                                <Icon size={14} src={Edit} />
                                                {translate(`${wishListLine.notes ? "Edit" : "Add"} Line Item Comment`)}
                                            </Link>
                                        </Hidden>
                                    </GridItem>
                                )}
                                {wishListLine.notes && (
                                    <GridItem {...styles.notesGridItem} className="wishlist-line-notes-grid-item">
                                        <SmallHeadingAndText
                                            heading={translate("Notes")}
                                            text={wishListLine.notes}
                                            extendedStyles={styles.notesStyles}
                                        />
                                    </GridItem>
                                )}

                                {showTextBadges && textBadges && (
                                    <ProductTextBadges
                                        badgeList={textBadges}
                                        maxBadges={maximumNumberOfTextBadges ?? 4}
                                        extendedStyles={styles.textBadgeStyles}
                                    />
                                )}
                                {showImageBadges && imageBadges && (
                                    <ProductImageBadges
                                        badgeList={imageBadges}
                                        maxBadges={maximumNumberOfImageBadges ?? 4}
                                        extendedStyles={styles.imageBadgeStyles}
                                        numberOfColumns={4}
                                    />
                                )}
                            </Hidden>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                {canEditWishList && (
                    <GridItem {...styles.overflowMenuGridItem}>
                        <OverflowMenu position="end">
                            <Clickable onClick={() => onEditNotesClick(wishListLine)}>
                                {translate(`${wishListLine.notes ? "Edit" : "Add"} Line Item Comment`)}
                            </Clickable>
                            <Clickable onClick={() => onDeleteClick(wishListLine)}>{translate("Delete")}</Clickable>
                        </OverflowMenu>
                    </GridItem>
                )}
            </GridContainer>
        </ProductContext.Provider>
    );
};

const mapWishListLineToProduct = (wishListLine: WishListLineModel) => {
    const product = cloneDeep(wishListLine) as any as ProductModel;
    product.id = wishListLine.productId;
    product.productNumber = wishListLine.erpNumber;
    product.productTitle = wishListLine.shortDescription;
    product.customerProductNumber = wishListLine.customerName;
    product.unitOfMeasures =
        wishListLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;
    return product;
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsProductListLine);
